import React, { useEffect, useMemo, useState } from "react";
import { Option, Select } from "@mui/joy";
import { DataGridPremium, GridColDef, GridToolbar } from "@mui/x-data-grid-premium";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import { toast } from "react-toastify";
import totalLocationData from "../../../../../data/locationdata";
import { getFetch, postFetch } from "../../../../../fetch/fetch";
import { englishLocationToKorean } from "../../../../../data/locationFuntion";
import CalendarModal from "../../calendarmodal";
import ImageModal2 from "../../../use/imagemodal2";

type AccessRow = {
    id: string | number;
    name: string;
    phone: string;
    location: string;
    value: string;
    accessType: string;
    accessAt: string;
    firstAccessAt: string;
    lastAccessAt: string;
    accessCount: number;
    holdingPeriod: string;
    unpaidAccess: string;
};

const LocalAccess: React.FC<any> = (props) => {
    const [selectedMonth, setSelectedMonth] = useState<string>(() => {
        const now = new Date();
        const year = now.getFullYear();
        const month = ("0" + (now.getMonth() + 1)).slice(-2);
        return `${year}-${month}`;
    });

    const [selectedLocation, setSelectedLocation] = useState<string>("");
    const [rows, setRows] = useState<AccessRow[]>([]);
    const [loading, setLoading] = useState<boolean>(false);

    // 학생/직원 필터
    const [roleFilter, setRoleFilter] = useState<string>("all");

    // 출입기록 달력 모달
    const [calOpen, setCalOpen] = useState<boolean>(false);
    const [selectedCalendarUserId, setSelectedCalendarUserId] = useState<number | null>(null);

    // 부정확 데이터 수동 맞추기 모달
    const [manualOpen, setManualOpen] = useState<boolean>(false);
    const [problematicOrderItems, setProblematicOrderItems] = useState<any[]>([]);
    const [manualSelections, setManualSelections] = useState<{ [key: number]: string }>({});
    const [savingId, setSavingId] = useState<number | null>(null);

    // 강제결제 증빙사진 뷰어
    const [imgOpen, setImgOpen] = useState<boolean>(false);
    const [imgSrc, setImgSrc] = useState<string>("");

    const locationOptions = useMemo(() => {
        return totalLocationData.filter(
            (each) =>
                each.academy?.includes("sunbae") &&
                each.english !== "total"
        );
    }, []);

    const monthOptions = useMemo(() => {
        return Array.from({ length: 12 }, (_, i) => {
            const now = new Date();
            const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
            const year = date.getFullYear();
            const month = ("0" + (date.getMonth() + 1)).slice(-2);
            return `${year}-${month}`;
        });
    }, []);

    const formatKST = (value: string) => {
        if (!value) return "";

        const date = new Date(value);

        if (Number.isNaN(date.getTime())) return value;

        // 한국시간으로 변환
        const kst = new Date(date.getTime() + 9 * 60 * 60 * 1000);

        const yyyy = kst.getFullYear();
        const mm = ("0" + (kst.getMonth() + 1)).slice(-2);
        const dd = ("0" + kst.getDate()).slice(-2);
        const hh = ("0" + kst.getHours()).slice(-2);
        const mi = ("0" + kst.getMinutes()).slice(-2);
        const ss = ("0" + kst.getSeconds()).slice(-2);

        return `${yyyy}-${mm}-${dd} ${hh}:${mi}:${ss}`;
    };

    // value(역할) → 한글
    const roleToKorean = (value: string) => {
        if (value === "student") return "학생";
        if (value === "teacher" || value === "staff") return "직원";
        if (value === "parent") return "학부모";
        return "미등록";
    };

    const columns: GridColDef[] = [
        {
            field: "id",
            headerName: "서버id",
            width: 100
        },
        {
            field: "name",
            headerName: "이름",
            width: 120
        },
        {
            field: "value",
            headerName: "신분",
            width: 80,
            valueFormatter: (params) => roleToKorean(params.value as string)
        },
        {
            field: "location",
            headerName: "지점",
            width: 90,
            valueFormatter: (params) => englishLocationToKorean(params.value as string)
        },
        {
            field: "firstAccessAt",
            headerName: "첫 출입 시간",
            width: 160,
            valueFormatter: (params) => formatKST(params.value as string),
        },
        {
            field: "lastAccessAt",
            headerName: "마지막 출입 시간",
            width: 160,
            valueFormatter: (params) => formatKST(params.value as string),
        },
        {
            field: "accessCount",
            headerName: "출입 횟수",
            width: 80
        },
        {
            field: "holdingPeriod",
            headerName: "보유기간",
            width: 260,
            renderCell: (params) => (
                <div style={{ whiteSpace: "normal", lineHeight: 1.4, padding: "6px 0" }}>
                    {params.value}
                </div>
            )
        },
        {
            field: "unpaidAccess",
            headerName: "미결제 출입기록",
            width: 230,
            renderCell: (params) => (
                <div style={{ whiteSpace: "normal", lineHeight: 1.4, padding: "6px 0", color: params.value ? "#d32f2f" : undefined }}>
                    {params.value}
                </div>
            )
        },
        {
            field: "calendar",
            headerName: "출입기록",
            width: 110,
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                const isMatched = typeof params.row.id === "number";
                return (
                    <Button
                        size="small"
                        variant="outlined"
                        disabled={!isMatched}
                        onClick={() => {
                            setSelectedCalendarUserId(params.row.id as number);
                            setCalOpen(true);
                        }}
                    >
                        출입기록
                    </Button>
                );
            }
        }
    ];

    useEffect(() => {
        if (!selectedMonth || !selectedLocation) {
            setRows([]);
            setProblematicOrderItems([]);
            return;
        }

        getLocalAccessList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedMonth, selectedLocation]);

    const getLocalAccessList = async () => {
        try {
            setLoading(true);

            const result: any = await getFetch(
                `/dashboard/payments/localaccesslist?selectedMonth=${selectedMonth}&location=${selectedLocation}`
            );

            const accessList = result?.accessList || result?.data || [];

            const mappedRows: AccessRow[] = accessList.map((item: any, index: number) => {
                const hasUser = !!item.id;

                return {
                    id: hasUser
                        ? item.id
                        : `biostar_${item.fingerprintId}`,
                    name: hasUser ? (item.name || "") : "",
                    value: item.value || "",
                    phone: item.phone || item.phoneNumber || "",
                    location: item.location || item.branch || "",
                    firstAccessAt: item.firstAccessAt || item.firstAccessTime || "",
                    lastAccessAt: item.lastAccessAt || item.lastAccessTime || "",
                    accessCount: item.accessCount || item.count || 0,
                    holdingPeriod: item.holdingPeriod || "",
                    unpaidAccess: item.unpaidAccess || "",
                    accessType: "",
                    accessAt: "",
                };
            });

            setRows(mappedRows);
            setProblematicOrderItems(result?.problematicOrderItems || []);
        } catch (e) {
            console.log(e);
            setRows([]);
            setProblematicOrderItems([]);
        } finally {
            setLoading(false);
        }
    };

    // 학생/직원 필터 적용
    const filteredRows = useMemo(() => {
        if (roleFilter === "all") return rows;
        if (roleFilter === "student") return rows.filter((r) => r.value === "student");
        return rows.filter((r) => r.value === "teacher" || r.value === "staff");
    }, [rows, roleFilter]);

    // 수동 지정 저장
    const saveManualLocation = async (orderItemId: number) => {
        const location = manualSelections[orderItemId];
        if (!location) {
            toast.warn("지점을 선택해주세요.");
            return;
        }

        try {
            setSavingId(orderItemId);
            const result: any = await postFetch("/dashboard/payments/localaccesslist/setmanuallocation", {
                orderItemId,
                location,
            });

            if (result.message === "success") {
                toast.success("결제 지점이 저장되었습니다.");
                await getLocalAccessList();
            } else {
                toast.error("저장 실패: " + result.message);
            }
        } catch (e) {
            console.log(e);
            toast.error("저장 중 오류가 발생했습니다.");
        } finally {
            setSavingId(null);
        }
    };

    const statusToText = (status: string) => {
        if (status === "unknown") return "지점 식별 불가 (강제결제/레거시)";
        if (status === "conflict") return "결제건 지점 모순 (분할결제 등)";
        return status;
    };

    return (
        <div style={{ marginTop: "24px" }}>
            <div
                style={{
                    display: "flex",
                    gap: "12px",
                    marginBottom: "20px",
                    flexWrap: "wrap",
                    alignItems: "center",
                }}
            >
                <Select
                    value={selectedMonth}
                    onChange={(e, newValue) => setSelectedMonth(newValue as string)}
                    placeholder="월 선택"
                    style={{ width: "160px" }}
                >
                    {monthOptions.map((month) => (
                        <Option key={month} value={month}>
                            {month}
                        </Option>
                    ))}
                </Select>

                <Select
                    value={selectedLocation}
                    onChange={(e, newValue) => setSelectedLocation(newValue as string)}
                    placeholder="지점 선택"
                    style={{ width: "160px" }}
                >
                    {locationOptions.map((location) => (
                        <Option key={location.english} value={location.english}>
                            {location.korean}
                        </Option>
                    ))}
                </Select>

                <ToggleButtonGroup
                    color="primary"
                    value={roleFilter}
                    exclusive
                    size="small"
                    onChange={(e, newValue) => { if (newValue) setRoleFilter(newValue); }}
                >
                    <ToggleButton value="all">전체</ToggleButton>
                    <ToggleButton value="student">학생</ToggleButton>
                    <ToggleButton value="teacher">직원</ToggleButton>
                </ToggleButtonGroup>

                <div style={{ marginLeft: "auto" }}>
                    <Button
                        variant="outlined"
                        color="warning"
                        onClick={() => setManualOpen(true)}
                    >
                        부정확 데이터 수동 맞추기 ({problematicOrderItems.length})
                    </Button>
                </div>
            </div>

            <div style={{ width: "100%" }}>
                <DataGridPremium
                    rows={filteredRows}
                    columns={columns}
                    loading={loading}
                    autoHeight
                    getRowHeight={() => "auto"}
                    disableRowSelectionOnClick
                    initialState={{
                        pagination: {
                            paginationModel: {
                                pageSize: 20,
                                page: 0,
                            },
                        },
                    }}
                    pageSizeOptions={[20, 50, 100]}
                    density="compact"
                    components={{ Toolbar: GridToolbar }}
                    sx={{
                        "& .MuiDataGrid-cell": {
                            paddingTop: "4px",
                            paddingBottom: "4px",
                        },
                    }}
                />
            </div>

            {/* 출입기록 달력 모달 */}
            <Modal open={calOpen} onClose={() => setCalOpen(false)}>
                <Box
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: 1260,
                        maxHeight: "92vh",
                        overflow: "auto",
                        bgcolor: "background.paper",
                        boxShadow: 24,
                        p: 4,
                    }}
                >
                    {selectedCalendarUserId && (
                        <CalendarModal
                            user={{ id: selectedCalendarUserId }}
                            initialYear={+selectedMonth.split("-")[0]}
                            initialMonth={+selectedMonth.split("-")[1] - 1}
                        />
                    )}
                </Box>
            </Modal>

            {/* 부정확 데이터 수동 맞추기 모달 */}
            <Modal open={manualOpen} onClose={() => setManualOpen(false)}>
                <Box
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: 920,
                        maxHeight: "88vh",
                        overflow: "auto",
                        bgcolor: "background.paper",
                        boxShadow: 24,
                        p: 4,
                    }}
                >
                    <div style={{ fontSize: "20px", fontWeight: 700, marginBottom: "8px" }}>
                        부정확 데이터 수동 맞추기
                    </div>
                    <div style={{ fontSize: "13px", color: "#666", marginBottom: "20px" }}>
                        결제기록만으로 결제 지점이 판별되지 않거나 모순되는 수강권입니다. 아래 결제기록을 보고 orderItem 단위로 지점을 지정하세요.
                    </div>

                    {problematicOrderItems.length === 0 && (
                        <div style={{ padding: "30px 0", textAlign: "center", color: "#888" }}>
                            현재 화면에 부정확 데이터가 없습니다.
                        </div>
                    )}

                    {problematicOrderItems.map((item: any) => (
                        <div
                            key={item.orderItemId}
                            style={{
                                border: "1px solid #e0e0e0",
                                borderRadius: "8px",
                                padding: "16px",
                                marginBottom: "16px",
                            }}
                        >
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <div style={{ fontWeight: 700, fontSize: "15px" }}>
                                    {item.studentName} <span style={{ color: "#999", fontWeight: 400 }}>(orderItem #{item.orderItemId})</span>
                                </div>
                                <div style={{ fontSize: "12px", color: "#d32f2f", fontWeight: 600 }}>
                                    {statusToText(item.status)}
                                </div>
                            </div>
                            <div style={{ fontSize: "13px", color: "#555", marginTop: "6px" }}>
                                등록지점: {item.studentLocation || "-"} &nbsp;|&nbsp; 기간: {item.startDate} ~ {item.endDate} &nbsp;|&nbsp; 금액: {Number(item.price).toLocaleString()}원 {item.isForced ? "(강제결제)" : ""}
                            </div>

                            <div style={{ marginTop: "12px", background: "#fafafa", borderRadius: "6px", padding: "10px" }}>
                                <div style={{ fontSize: "12px", fontWeight: 600, marginBottom: "6px", color: "#666" }}>결제기록</div>
                                {(item.paymentRecords || []).map((rec: any) => {
                                    const idKey = rec.payTeacherBillId || rec.terminalKey || rec.transRefKey || "-";
                                    const cand = (rec.candidates || []).map((c: string) => englishLocationToKorean(c)).join(", ");
                                    return (
                                        <div key={rec.id} style={{ fontSize: "12px", color: rec.canceled ? "#aaa" : "#333", marginBottom: "3px" }}>
                                            • {rec.type} / {Number(rec.price).toLocaleString()}원 / {formatKST(rec.createdAt)} {rec.canceled ? "(취소됨)" : ""}
                                            <span style={{ color: "#888" }}> &nbsp; key: {idKey}</span>
                                            <span style={{ color: cand ? "#2e7d32" : "#d32f2f" }}> &nbsp; 후보: {cand || "식별불가"}</span>
                                            {rec.forcedImg && (
                                                <span
                                                    onClick={() => { setImgSrc(rec.forcedImg); setImgOpen(true); }}
                                                    style={{ color: "#1565c0", textDecoration: "underline", cursor: "pointer", marginLeft: "8px" }}
                                                >
                                                    증빙사진 보기
                                                </span>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                            <div style={{ display: "flex", gap: "10px", alignItems: "center", marginTop: "12px" }}>
                                <Select
                                    value={manualSelections[item.orderItemId] || ""}
                                    onChange={(e, newValue) =>
                                        setManualSelections((prev) => ({ ...prev, [item.orderItemId]: newValue as string }))
                                    }
                                    placeholder="지점 지정"
                                    style={{ width: "200px" }}
                                    slotProps={{ listbox: { sx: { zIndex: 1600 } } }}
                                >
                                    {locationOptions.map((location) => (
                                        <Option key={location.english} value={location.english}>
                                            {location.korean}
                                        </Option>
                                    ))}
                                </Select>
                                <Button
                                    variant="contained"
                                    disabled={savingId === item.orderItemId}
                                    onClick={() => saveManualLocation(item.orderItemId)}
                                >
                                    저장
                                </Button>
                            </div>
                        </div>
                    ))}
                </Box>
            </Modal>

            {/* 강제결제 증빙사진 뷰어 */}
            <ImageModal2
                open={imgOpen}
                handleClose={() => setImgOpen(false)}
                src={imgSrc ? [imgSrc] : []}
            />
        </div>
    );
};

export default LocalAccess;
